import React from 'react';

import { CalendarToday as CalendarTodayIcon, Clear as ClearIcon } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  Grow,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';

import { DateFormatPatterns } from '@clearverify/constants';

import { SxProp } from 'src/types/mui';

import { DateRangePickerProps } from './interface';

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  format = DateFormatPatterns.shortDateWithSlash,
  name,
  disabled = false,
  minDate,
  maxDate,
  textFieldProps,
}) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const popperId = React.useId();

  const [start, end] = value;
  const hasValue = Boolean(start || end);

  React.useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const displayValue = React.useMemo(() => {
    if (!start && !end) return '';
    const formattedStart = start ? start.toFormat(format) : '';
    const formattedEnd = end ? end.toFormat(format) : '';
    return `${formattedStart} – ${formattedEnd}`;
  }, [start, end, format]);

  // Cross-constraints make an inverted pick impossible; if the current value already sits
  // outside [minDate, maxDate], fall back to the global bound so the calendar isn't dead.
  let fromMaxDate = end && maxDate ? DateTime.min(end, maxDate) : (end ?? maxDate);
  if (minDate && fromMaxDate && fromMaxDate < minDate) fromMaxDate = maxDate;
  let toMinDate = start && minDate ? DateTime.max(start, minDate) : (start ?? minDate);
  if (maxDate && toMinDate && toMinDate > maxDate) toMinDate = minDate;

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const closeAndRefocus = () => {
    handleClose();
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      handleOpen();
    } else if (event.key === 'Escape') {
      handleClose();
    }
  };

  const handlePopperKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') closeAndRefocus();
  };

  const handleClear = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onChange([null, null]);
    inputRef.current?.focus();
  };

  const handleStartChange = (date: DateTime | null) => onChange([date, end]);

  const handleEndChange = (date: DateTime | null) => {
    onChange([start, date]);
    // only a To pick auto-closes — closing on any completed pair would slam the
    // popper shut on the first click when re-editing an existing full range.
    if (start && date) handleClose();
  };

  return (
    <React.Fragment>
      <TextField
        name={name}
        value={displayValue}
        disabled={disabled}
        ref={rootRef}
        inputRef={inputRef}
        onClick={handleOpen}
        slotProps={{
          htmlInput: {
            readOnly: true,
            onKeyDown: handleInputKeyDown,
            'aria-haspopup': 'dialog',
            'aria-expanded': open,
            'aria-controls': open ? popperId : undefined,
            sx: { cursor: 'inherit' },
          },
          input: {
            // The whole field is a click target; the root's default cursor is `text` and the
            // bare calendar SvgIcon inherits it, so pointer must be set at the root level.
            sx: { cursor: disabled ? 'default' : 'pointer' },
            endAdornment: (
              <InputAdornment position="end">
                {hasValue && !disabled && (
                  <IconButton aria-label="Clear date range" size="small" onClick={handleClear}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <CalendarTodayIcon fontSize="small" color={disabled ? 'disabled' : 'action'} />
              </InputAdornment>
            ),
          },
        }}
        {...textFieldProps}
      />
      <Popper
        open={open}
        anchorEl={rootRef.current}
        placement="bottom-start"
        transition
        sx={popperStyles}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              id={popperId}
              role="dialog"
              aria-label="Choose date range"
              elevation={8}
              onKeyDown={handlePopperKeyDown}
              sx={paperStyles}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box sx={calendarsWrapperStyles}>
                  <Box role="group" aria-label="From date">
                    <Typography variant="subtitle2" sx={calendarLabelStyles}>
                      From
                    </Typography>
                    <DateCalendar
                      value={start}
                      referenceDate={end ?? undefined}
                      minDate={minDate}
                      maxDate={fromMaxDate}
                      disabled={disabled}
                      onChange={handleStartChange}
                    />
                  </Box>
                  <Box role="group" aria-label="To date">
                    <Typography variant="subtitle2" sx={calendarLabelStyles}>
                      To
                    </Typography>
                    <DateCalendar
                      value={end}
                      referenceDate={start ?? undefined}
                      minDate={toMinDate}
                      maxDate={maxDate}
                      disabled={disabled}
                      onChange={handleEndChange}
                    />
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};

const popperStyles: SxProp = {
  zIndex: 'modal',
};

const paperStyles: SxProp = {
  maxWidth: 'calc(100vw - 32px)',
};

const calendarsWrapperStyles: SxProp = {
  display: 'flex',
  flexWrap: 'wrap',
};

const calendarLabelStyles: SxProp = {
  pt: 2,
  px: 3,
};
